import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaApple, FaAndroid, FaUserPlus, FaStar, FaDollarSign, FaChartLine, FaChartBar,
  FaClipboardList, FaLink, FaCreditCard, FaUniversity, FaPaypal,
  FaCheckCircle, FaExclamationTriangle, FaRegCalendarAlt, FaFlask, FaUndo,
  FaArrowUp, FaArrowDown, FaRocket, FaLayerGroup, FaMobileAlt,
  IconType,
} from '../lib/icons';
import { fetchInfluencerDashboard, fetchPromoMaterials, fetchInfluencerProfile, updateInfluencerProfile, logoutInfluencer, InfluencerDashboard as DashboardData, PromoMaterials, PaymentProfile } from '../lib/influencer-api';
import logo from '../assets/logo.png';

interface Props {
  setIsInfluencerLoggedIn: (val: boolean) => void;
}

export default function InfluencerDashboard({ setIsInfluencerLoggedIn }: Props) {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [promo, setPromo] = useState<PromoMaterials | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [profile, setProfile] = useState<PaymentProfile | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [d, p] = await Promise.all([fetchInfluencerDashboard(), fetchPromoMaterials()]);
        setDashboard(d);
        setPromo(p);
      } catch {
        logoutInfluencer();
        setIsInfluencerLoggedIn(false);
        navigate('/influencer');
      } finally { setLoading(false); }
    };
    load();
  }, [navigate, setIsInfluencerLoggedIn]);

  const handleLogout = () => {
    logoutInfluencer();
    setIsInfluencerLoggedIn(false);
    navigate('/influencer');
  };

  // Load payment profile when the tab is opened
  useEffect(() => {
    if (activeTab === 'profile' && !profile) {
      fetchInfluencerProfile().then(setProfile).catch(() => {});
    }
  }, [activeTab, profile]);

  const saveProfile = async () => {
    if (!profile) return;
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      await updateInfluencerProfile({
        country: profile.country,
        payment_method: profile.payment_method,
        payment_email: profile.payment_email,
        bank_name: profile.bank_name,
        bank_account_holder: profile.bank_account_holder,
        bank_account_number: profile.bank_account_number,
        bank_routing_swift: profile.bank_routing_swift,
        tax_id: profile.tax_id,
      });
      setProfileMsg({ type: 'success', text: 'Payment details saved successfully' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to save' });
    } finally {
      setProfileSaving(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-3 border-primary border-t-transparent"></div>
    </div>
  );

  if (!dashboard) return null;

  const { influencer, stats, commission, monthly, recent_events } = dashboard;
  const monthlyEntries = Object.entries(monthly).sort((a, b) => b[0].localeCompare(a[0]));

  const tabs: { id: string; label: string; Icon: IconType }[] = [
    { id: 'overview',   label: 'Overview',   Icon: FaChartBar },
    { id: 'commission', label: 'Commission', Icon: FaDollarSign },
    { id: 'promo',      label: 'Promo',      Icon: FaLink },
    { id: 'activity',   label: 'Activity',   Icon: FaClipboardList },
    { id: 'profile',    label: 'Payment',    Icon: FaCreditCard },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page header strip */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 md:py-5">
          <div className="flex justify-between items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-orange-500 text-white flex items-center justify-center text-lg font-bold shadow-md shadow-primary/20">
                {influencer.name[0].toUpperCase()}
              </div>
              <div>
                <div className="text-[11px] text-gray-500 font-medium">Welcome back,</div>
                <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">{influencer.name}</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono text-[11px]">{influencer.code}</span>
                  <span className="text-gray-400 text-[11px] capitalize inline-flex items-center gap-1"><FaMobileAlt /> {influencer.platform}</span>
                </div>
              </div>
            </div>
            <button onClick={handleLogout} className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-4 py-1.5 font-semibold transition-colors">
              Log out
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip on white background */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {([
              { label: 'Total Signups', value: stats.total_signups, IconC: FaUserPlus,
                bg: 'from-blue-50 to-blue-100/40', ring: 'ring-blue-100', text: 'text-blue-700', iconText: 'text-blue-400',
                delta: stats.deltas?.signups },
              { label: 'Trials', value: stats.trials ?? 0, IconC: FaFlask,
                bg: 'from-yellow-50 to-amber-100/40', ring: 'ring-yellow-100', text: 'text-amber-700', iconText: 'text-amber-400',
                delta: stats.deltas?.trials },
              { label: 'Paid', value: stats.paid_conversions ?? stats.total_subscriptions, IconC: FaStar,
                bg: 'from-purple-50 to-fuchsia-100/40', ring: 'ring-purple-100', text: 'text-purple-700', iconText: 'text-purple-400',
                delta: stats.deltas?.paid },
              { label: 'Earned', value: `${commission.currency} ${commission.earned.toFixed(2)}`, IconC: FaDollarSign,
                bg: 'from-orange-50 to-amber-100/40', ring: 'ring-orange-100', text: 'text-orange-700', iconText: 'text-orange-400',
                delta: undefined as number | undefined },
            ] as const).map((s) => {
              const delta = s.delta;
              const hasDelta = typeof delta === 'number' && delta !== 0;
              const positive = (delta ?? 0) > 0;
              const Icon = s.IconC;
              return (
                <div
                  key={s.label}
                  className={`relative overflow-hidden rounded-2xl px-4 py-3.5 bg-gradient-to-br ${s.bg} ring-1 ${s.ring} shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">{s.label}</div>
                    <Icon className={`text-lg ${s.iconText}`} />
                  </div>
                  <div className={`text-2xl md:text-[1.7rem] font-bold mt-1 ${s.text}`}>{s.value}</div>
                  {hasDelta ? (
                    <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${positive ? 'text-green-600' : 'text-red-600'}`}>
                      {positive ? <FaArrowUp /> : <FaArrowDown />}
                      <span>{positive ? '+' : ''}{delta}</span>
                      <span className="text-gray-400 font-normal">vs last month</span>
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px] text-gray-400">—</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1.5 mb-6 shadow-sm border border-gray-100 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.Icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${active ? 'bg-primary text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              >
                <Icon className={active ? 'text-white' : 'text-gray-400'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Performance with stacked bars */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-text flex items-center gap-2"><FaRegCalendarAlt className="text-gray-500" /> Monthly Performance</h3>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400"></span>Signups</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400"></span>Trials</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-purple-500"></span>Paid</span>
                </div>
              </div>
              {monthlyEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-300">
                  <FaChartBar className="text-4xl mx-auto mb-2" />
                  <p className="text-sm">No data yet. Share your links to get started!</p>
                </div>
              ) : (() => {
                const slice = monthlyEntries.slice(0, 6).reverse();
                const max = Math.max(1, ...slice.map(([, d]: any) => Math.max(d.signups || 0, (d.trials || 0) + (d.subscriptions || 0))));
                return (
                  <div className="space-y-4">
                    {slice.map(([month, data]: any) => {
                      const s = data.signups || 0;
                      const t = data.trials || 0;
                      const p = data.subscriptions || 0;
                      return (
                        <div key={month}>
                          <div className="flex justify-between items-baseline mb-1.5">
                            <span className="text-sm text-gray-700 font-medium">
                              {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </span>
                            <span className="text-[11px] text-gray-400">
                              {s} signups · {t} trials · {p} paid
                            </span>
                          </div>
                          <div className="grid gap-1">
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${(s / max) * 100}%` }} />
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${(t / max) * 100}%` }} />
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${(p / max) * 100}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Right column: Funnel + Device + Quick Copy */}
            <div className="space-y-6">

            {/* Conversion Funnel */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-text flex items-center gap-2"><FaLayerGroup className="text-gray-500" /> Conversion Funnel</h3>
                <span className="text-[11px] text-gray-400">All-time</span>
              </div>
              {(() => {
                const sCount = stats.total_signups || 0;
                const tCount = stats.trials || 0;
                const pCount = stats.paid_conversions ?? stats.total_subscriptions ?? 0;
                const max = Math.max(sCount, 1);
                const steps = [
                  { label: 'Signups', value: sCount, color: 'from-blue-400 to-blue-500',   IconC: FaUserPlus },
                  { label: 'Trials',  value: tCount, color: 'from-amber-400 to-amber-500', IconC: FaFlask },
                  { label: 'Paid',    value: pCount, color: 'from-purple-500 to-fuchsia-500', IconC: FaStar },
                ];
                return (
                  <div className="space-y-3">
                    {steps.map((step, i) => {
                      const pct = Math.min(100, (step.value / max) * 100);
                      // Percentage relative to total signups (true funnel rate)
                      const dropPct = i === 0 ? null
                        : sCount > 0 ? Math.min(100, Math.round((step.value / sCount) * 100))
                        : null;
                      const StepIcon = step.IconC;
                      return (
                        <div key={step.label}>
                          <div className="flex items-center justify-between mb-1.5 text-xs">
                            <span className="font-semibold text-gray-700 inline-flex items-center gap-1.5"><StepIcon className="text-gray-500" /> {step.label}</span>
                            <span className="text-gray-500">
                              {step.value}
                              {dropPct !== null && (
                                <span className="ml-2 text-gray-400">
                                  ({dropPct}% of signups)
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r ${step.color} transition-all duration-500`}
                              style={{ width: `${Math.max(pct, 3)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Device Breakdown */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-text mb-4 flex items-center gap-2"><FaMobileAlt className="text-gray-500" /> Device Breakdown</h3>
                <div className="space-y-4">
                  {(() => {
                    const totalDevices = (stats.devices.ios || 0) + (stats.devices.android || 0) + (stats.devices.unknown || 0) || 1;
                    return [
                      { label: 'iOS', value: stats.devices.ios, IconC: FaApple, color: 'bg-gray-800', totalDevices },
                      { label: 'Android', value: stats.devices.android, IconC: FaAndroid, color: 'bg-green-500', totalDevices },
                    ];
                  })().map((d) => {
                    const pct = Math.round((d.value / d.totalDevices) * 100);
                    const Icon = d.IconC;
                    return (
                      <div key={d.label}>
                        <div className="flex justify-between mb-1.5">
                          <span className="text-sm font-medium inline-flex items-center gap-2"><Icon className="text-gray-600" /> {d.label}</span>
                          <span className="text-sm text-gray-500 font-semibold">{d.value} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div className={`${d.color} h-2.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick Copy Links */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="font-bold text-text mb-3 flex items-center gap-2"><FaLink className="text-gray-500" /> Quick Copy</h3>
                <div className="space-y-2">
                  {promo?.apple_link && (
                    <button onClick={() => copyToClipboard(promo.apple_link, 'apple')}
                      className="w-full text-left text-sm px-4 py-3 bg-gray-50 rounded-xl hover:bg-primary/5 hover:border-primary/20 transition-all border border-transparent truncate flex items-center gap-2">
                      <FaApple className="text-gray-700 shrink-0" /> {copied === 'apple' ? <span className="text-green-500 font-medium inline-flex items-center gap-1"><FaCheckCircle /> Copied!</span> : <span className="text-gray-500 truncate">{promo.apple_link}</span>}
                    </button>
                  )}
                  {promo?.google_link && (
                    <button onClick={() => copyToClipboard(promo.google_link, 'google')}
                      className="w-full text-left text-sm px-4 py-3 bg-gray-50 rounded-xl hover:bg-primary/5 hover:border-primary/20 transition-all border border-transparent truncate flex items-center gap-2">
                      <FaAndroid className="text-green-600 shrink-0" /> {copied === 'google' ? <span className="text-green-500 font-medium inline-flex items-center gap-1"><FaCheckCircle /> Copied!</span> : <span className="text-gray-500 truncate">{promo.google_link}</span>}
                    </button>
                  )}
                  <button onClick={() => promo && copyToClipboard(promo.code, 'qcode')}
                    className="w-full text-left text-sm px-4 py-3 bg-primary/5 rounded-xl hover:bg-primary/10 transition-all border border-primary/10 flex items-center gap-2">
                    <FaLink className="text-gray-500 shrink-0" /> {copied === 'qcode' ? <span className="text-green-500 font-medium inline-flex items-center gap-1"><FaCheckCircle /> Copied!</span> : <span className="text-primary font-mono font-bold">{promo?.code}</span>}
                  </button>
                </div>
              </div>

            </div> {/* /right column */}
          </div>
        )}

        {/* Commission Tab */}
        {activeTab === 'commission' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 text-center border border-green-200">
                <p className="text-4xl font-bold text-green-600">{commission.currency} {commission.earned.toFixed(2)}</p>
                <p className="text-green-700 text-sm mt-2 font-medium inline-flex items-center gap-1.5"><FaDollarSign /> Total Earned</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 text-center border border-blue-200">
                <p className="text-4xl font-bold text-blue-600">{commission.currency} {commission.paid.toFixed(2)}</p>
                <p className="text-blue-700 text-sm mt-2 font-medium inline-flex items-center gap-1.5"><FaCheckCircle /> Paid Out</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 text-center border border-orange-200">
                <p className="text-4xl font-bold text-primary">{commission.currency} {commission.pending.toFixed(2)}</p>
                <p className="text-orange-700 text-sm mt-2 font-medium">⏳ Pending</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-text mb-4">Commission Details</h3>
              <div className="space-y-3">
                {[
                  { label: 'Commission Rate', value: `${commission.rate}% on first paid purchase`, IconC: FaChartBar },
                  { label: 'Paid Conversions', value: stats.paid_conversions ?? stats.total_subscriptions, IconC: FaStar },
                  { label: 'Active Trials', value: stats.trials ?? 0, IconC: FaFlask },
                  { label: 'Conversion Rate', value: `${stats.conversion_rate}%`, IconC: FaChartLine },
                ].map((item) => {
                  const ItemIcon = item.IconC;
                  return (
                  <div key={item.label} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                    <span className="text-sm text-gray-500 inline-flex items-center gap-2"><ItemIcon className="text-gray-400" /> {item.label}</span>
                    <span className="font-bold text-text">{item.value}</span>
                  </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4 bg-gray-50 p-3 rounded-xl">Commissions are calculated automatically. Payouts are processed monthly. Contact support@eatmeatapp.com for payout inquiries.</p>
            </div>
          </div>
        )}

        {/* Promo Tab */}
        {activeTab === 'promo' && promo && (
          <div className="space-y-6">
            {/* Links */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-text mb-5 inline-flex items-center gap-2"><FaMobileAlt className="text-gray-500" /> Your Download Links</h3>
              <div className="space-y-4">
                {[
                  { IconC: FaApple, iconColor: 'text-gray-700', label: 'iOS (App Store)', link: promo.apple_link, key: 'a2' },
                  { IconC: FaAndroid, iconColor: 'text-green-600', label: 'Android (Google Play)', link: promo.google_link, key: 'g2' },
                ].map((item) => {
                  const ItemIcon = item.IconC;
                  return (
                  <div key={item.key} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <ItemIcon className={`text-3xl ${item.iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                      <p className="text-sm truncate font-mono text-gray-600 mt-0.5">{item.link}</p>
                    </div>
                    <button onClick={() => copyToClipboard(item.link, item.key)}
                      className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0 inline-flex items-center gap-1.5 ${copied === item.key ? 'bg-green-500 text-white' : 'bg-primary text-white hover:shadow-lg hover:shadow-primary/25'}`}>
                      {copied === item.key ? <><FaCheckCircle /> Copied</> : 'Copy'}
                    </button>
                  </div>
                  );
                })}
                <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl border border-primary/10">
                  <FaLink className="text-3xl text-primary" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-400 font-medium">Referral Code</p>
                    <p className="text-2xl font-mono font-bold text-primary mt-0.5 tracking-wider">{promo.code}</p>
                  </div>
                  <button onClick={() => copyToClipboard(promo.code, 'c2')}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${copied === 'c2' ? 'bg-green-500 text-white' : 'bg-primary text-white hover:shadow-lg hover:shadow-primary/25'}`}>
                    {copied === 'c2' ? <span className="inline-flex items-center gap-1.5"><FaCheckCircle /> Copied</span> : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            {/* Captions */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-text mb-2">✍️ Sample Captions</h3>
              <p className="text-sm text-gray-400 mb-5">Copy and customize for your posts</p>
              <div className="space-y-4">
                {promo.sample_captions.map((caption) => (
                  <div key={caption.platform} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="flex justify-between items-center px-4 py-3 bg-gray-50">
                      <span className="text-sm font-semibold capitalize text-gray-600">
                        {caption.platform === 'general' ? '📝 General' : caption.platform === 'instagram' ? '📸 Instagram' : caption.platform === 'youtube' ? '▶️ YouTube' : '🎵 TikTok'}
                      </span>
                      <button onClick={() => copyToClipboard(caption.text, caption.platform)}
                        className={`text-sm font-medium px-3 py-1 rounded-lg transition-all ${copied === caption.platform ? 'bg-green-100 text-green-600' : 'text-primary hover:bg-primary/10'}`}>
                        {copied === caption.platform ? <span className="inline-flex items-center gap-1.5"><FaCheckCircle /> Copied!</span> : 'Copy'}
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 whitespace-pre-line p-4 leading-relaxed">{caption.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Payment Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="font-bold text-text flex items-center gap-2"><FaCreditCard className="text-gray-500" /> Payment Profile</h3>
              <p className="text-sm text-gray-400 mt-1">Update how you receive your commission payouts. Your details are encrypted.</p>
            </div>
            {!profile ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {profileMsg && (
                  <div className={`p-3 rounded-xl text-sm font-semibold ${profileMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                    <span className="inline-flex items-center gap-1.5">{profileMsg.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}{profileMsg.text}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Country</label>
                    <input type="text" value={profile.country || ''} onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                      placeholder="United States" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Tax ID (optional)</label>
                    <input type="text" value={profile.tax_id || ''} onChange={(e) => setProfile({ ...profile, tax_id: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                      placeholder="For tax reporting" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Payment Method</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { key: 'paypal',        label: 'PayPal',   IconC: FaPaypal },
                      { key: 'wise',          label: 'Wise',     IconC: FaCreditCard },
                      { key: 'payoneer',      label: 'Payoneer', IconC: FaCreditCard },
                      { key: 'bank_transfer', label: 'Bank',     IconC: FaUniversity },
                    ].map((m) => {
                      const MIcon = m.IconC;
                      const isActive = profile.payment_method === m.key;
                      return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setProfile({ ...profile, payment_method: m.key })}
                        className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-bold transition-all ${isActive ? 'bg-gradient-to-br from-primary to-orange-500 text-white shadow-md scale-105' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                      >
                        <MIcon className="text-lg" />
                        <span>{m.label}</span>
                      </button>
                      );
                    })}
                  </div>
                </div>

                {profile.payment_method === 'bank_transfer' ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Bank Name</label>
                        <input type="text" value={profile.bank_name || ''} onChange={(e) => setProfile({ ...profile, bank_name: e.target.value })}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none text-sm"
                          placeholder="Chase Bank" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Account Holder</label>
                        <input type="text" value={profile.bank_account_holder || ''} onChange={(e) => setProfile({ ...profile, bank_account_holder: e.target.value })}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none text-sm"
                          placeholder="Full name on account" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Account Number / IBAN</label>
                        <input type="text" value={profile.bank_account_number || ''} onChange={(e) => setProfile({ ...profile, bank_account_number: e.target.value })}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none text-sm font-mono"
                          placeholder="1234567890 or IBAN" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Routing / SWIFT / BIC</label>
                        <input type="text" value={profile.bank_routing_swift || ''} onChange={(e) => setProfile({ ...profile, bank_routing_swift: e.target.value })}
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none text-sm font-mono"
                          placeholder="021000021 or BOFAUS3N" />
                      </div>
                    </div>
                  </div>
                ) : profile.payment_method ? (
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                      {profile.payment_method === 'paypal' ? 'PayPal Email' : profile.payment_method === 'wise' ? 'Wise Email' : profile.payment_method === 'payoneer' ? 'Payoneer Email' : 'Payment Email'}
                    </label>
                    <input type="email" value={profile.payment_email || ''} onChange={(e) => setProfile({ ...profile, payment_email: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                      placeholder="you@example.com" />
                  </div>
                ) : null}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={saveProfile}
                    disabled={profileSaving}
                    className="px-6 py-2.5 bg-gradient-to-r from-primary to-orange-500 text-white font-bold rounded-xl shadow-md shadow-primary/20 hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 transition-all flex items-center gap-2"
                  >
                    {profileSaving ? (<><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>) : (<><FaCheckCircle /> Save Changes</>)}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-50">
              <h3 className="font-bold text-text inline-flex items-center gap-2"><FaClipboardList className="text-gray-500" /> Recent Activity</h3>
              <p className="text-sm text-gray-400 mt-1">{recent_events.length} events</p>
            </div>
            {recent_events.length === 0 ? (
              <div className="p-12 text-center">
                <FaRocket className="text-4xl mx-auto mb-3 text-gray-300" />
                <p className="text-gray-400 text-sm">No activity yet. Share your links to get started!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recent_events.map((event, i) => {
                  const isTrial = event.is_trial === true || event.event_type === 'trial';
                  const isPaid = event.is_first_paid === true;
                  const isRefund = event.event_type === 'subscription' && !isPaid && (event.commission_amount || 0) < 0;
                  const label = isRefund ? 'Refund / reversal'
                    : isTrial ? 'Trial started'
                    : isPaid ? 'Paid conversion'
                    : event.event_type.replace('_', ' ');
                  const tint = isRefund ? 'bg-red-100'
                    : isTrial ? 'bg-yellow-100'
                    : isPaid ? 'bg-primary/10'
                    : event.event_type === 'signup' ? 'bg-green-100'
                    : 'bg-gray-100';
                  const IconC: IconType = isRefund ? FaUndo
                    : isTrial ? FaFlask
                    : isPaid ? FaStar
                    : event.event_type === 'signup' ? FaUserPlus
                    : FaMobileAlt;
                  return (
                  <div key={i} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-lg ${tint}`}>
                      <IconC className="text-gray-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text capitalize">{label}</p>
                      <p className="text-xs text-gray-400 mt-0.5 inline-flex items-center gap-1.5">
                        <span>{event.user?.username || 'Anonymous'}</span>
                        <span>•</span>
                        {event.device_type === 'ios' ? <span className="inline-flex items-center gap-1"><FaApple /> iOS</span>
                          : event.device_type === 'android' ? <span className="inline-flex items-center gap-1"><FaAndroid /> Android</span>
                          : <span>Unknown</span>}
                        {event.provider_store ? <><span>•</span><span>{event.provider_store.replace('_', ' ')}</span></> : null}
                      </p>
                      {(isPaid || isRefund) && (
                        <p className={`text-xs mt-0.5 font-semibold ${isRefund ? 'text-red-600' : 'text-green-600'}`}>
                          {event.commission_amount < 0 ? '-' : '+'}{event.currency} {Math.abs(event.commission_amount).toFixed(2)}
                          <span className="text-gray-300 font-normal"> on {event.currency} {Math.abs(event.amount).toFixed(2)}</span>
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-300 flex-shrink-0 font-medium">
                      {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
